import type {
  CaptureInput,
  CaptureReceipt,
  Folder,
  ItemDetail,
  ItemPatch,
  LibraryItem,
  Page,
  Tag,
  User,
  BatchPatchInput,
} from '@kiipu/app-protocol';
import { canMutate, desktopBridge, isDesktop, platformState } from './lib/client-platform';
import { startDesktopCapture } from './lib/desktop-captures';
import {
  clearDesktopCache,
  desktopCacheAccountIdentity,
  desktopCacheIdentity,
  desktopCacheKind,
  readDesktopCache,
  revokeDesktopCacheResource,
  writeDesktopCache,
} from './lib/desktop-cache';
export type {
  Folder,
  Tag,
  User,
  LibraryItem,
  ItemDetail,
  ItemPatch,
  BatchPatchInput,
} from '@kiipu/app-protocol';

export type Session = {
  user: User | null;
  csrfToken: string;
  turnstileSiteKey?: string;
  registrationOpen: boolean;
};
export type Settings = { theme: 'light' | 'dark' | 'system'; locale: string };
export type LibrarySearchInput = {
  q: string;
  folderId?: string;
  tagId?: string;
  favorite?: boolean;
  platform?: LibraryItem['platform'];
  kind?: LibraryItem['kind'];
  quality?: LibraryItem['quality'];
  status?: LibraryItem['status'];
  trash?: boolean;
  limit?: number;
  cursor?: string;
};
export type Usage = {
  itemCount: number;
  storageBytes: number;
  capturesToday: number;
  limits: {
    items: number;
    storageBytes: number;
    capturesPerDay: number;
    trashDays?: number;
  };
};
export type DeviceSession = {
  id: string;
  kind: string;
  name: string;
  createdAt: string;
  lastUsedAt: string;
  current?: boolean;
};
export type AccountLinkProvider = 'google' | 'github';
export type AccountLinkAction = 'link' | 'unlink';
export type AccountLinkIdentity = {
  id: string;
  provider: string;
  accountLabel?: string;
  available: boolean;
  canUnlink: boolean;
  requiresPasswordToUnlink: boolean;
};
export type AccountLinksOverview = {
  passwordEnabled: boolean;
  email: string;
  emailVerified: boolean;
  stepUpMethods: Array<'password' | 'email_code'>;
  items: AccountLinkIdentity[];
};
export type AccountLinkChallenge = { challengeId: string; expiresAt: string };
export type AccountLinkVerification = { stepUpToken: string; expiresAt: string };
export type AccountLinkTarget = { action: AccountLinkAction; identityId?: string };
export type AccountLinkProof = AccountLinkTarget &
  (
    | { method: 'password'; password: string }
    | { method: 'email_code'; challengeId: string; code: string }
  );
let csrfToken = '';
export class APIError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: unknown,
  ) {
    super(message);
  }
}
export async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const desktop = isDesktop();
  const fullPath = `/api/v1${path}`;
  const method = (options.method || 'GET').toUpperCase();
  const mutating =
    !['GET', 'HEAD', 'OPTIONS'].includes(method) && !(path === '/search' && method === 'POST');
  // Feedback does not change library data, including automatic read receipts.
  const invalidatesLibrary =
    mutating && !/^\/(?:feedback|collection-filter-preferences)(?:[/?]|$)/.test(path);
  const identity = desktopCacheAccountIdentity();
  const cacheIdentity = desktopCacheIdentity();
  const kind = desktop && method === 'GET' ? desktopCacheKind(fullPath) : null;
  if (desktop && mutating && !canMutate())
    throw new APIError('当前处于离线阅读模式，请联网后再进行修改。', 503, 'desktop_offline');
  if (desktop && platformState()?.online === false) {
    if (kind) {
      const value = await readDesktopCache<T>(fullPath);
      options.signal?.throwIfAborted();
      if (value !== null) return value;
    }
    throw new APIError('此内容尚未缓存，请联网后打开。', 503, 'desktop_offline');
  }
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData))
    headers.set('Content-Type', 'application/json');
  if (options.method && !['GET', 'HEAD'].includes(options.method))
    headers.set('X-CSRF-Token', csrfToken);
  const response = await fetch(fullPath, {
    ...options,
    credentials: 'include',
    headers,
  });
  options.signal?.throwIfAborted();
  if (
    desktop &&
    (identity !== desktopCacheAccountIdentity() ||
      (kind && cacheIdentity !== desktopCacheIdentity()))
  )
    throw new DOMException('Account changed during request', 'AbortError');
  if (kind && response.headers.get('x-kiipu-desktop-offline') === '1') {
    const value = await readDesktopCache<T>(fullPath);
    if (value !== null) return value;
  }
  if (response.status === 204) {
    if (desktop && invalidatesLibrary) await clearDesktopCache().catch(() => undefined);
    return undefined as T;
  }
  const data = await response.json().catch(() => null);
  options.signal?.throwIfAborted();
  if (
    desktop &&
    (identity !== desktopCacheAccountIdentity() ||
      (kind && cacheIdentity !== desktopCacheIdentity()))
  )
    throw new DOMException('Account changed during request', 'AbortError');
  if (desktop && kind && [401, 403, 404, 410].includes(response.status))
    await revokeDesktopCacheResource(fullPath).catch(() => undefined);
  if (
    response.status === 401 &&
    path !== '/session' &&
    (!path.startsWith('/auth/') || /^\/auth\/account-links(?:[/?]|$)/.test(path))
  ) {
    window.dispatchEvent(new CustomEvent('kiipu:session-expired'));
  }
  if (!response.ok)
    throw new APIError(
      data?.error?.message || data?.message || `请求未完成（${response.status}）`,
      response.status,
      data?.error?.code,
      data?.error?.details,
    );
  if (data?.csrfToken) csrfToken = data.csrfToken;
  if (kind && response.ok && data && typeof data === 'object')
    await writeDesktopCache(fullPath, data, kind);
  if (desktop && response.ok && invalidatesLibrary)
    await clearDesktopCache().catch(() => undefined);
  return data as T;
}
const json = (method: string, value?: unknown): RequestInit => ({
  method,
  ...(value === undefined ? {} : { body: JSON.stringify(value) }),
});
export const api = {
  session: () =>
    isDesktop()
      ? Promise.resolve<Session>({
          user: platformState()?.user ?? null,
          csrfToken: '',
          registrationOpen: true,
        })
      : request<Session>('/session'),
  login: (input: { email: string; password: string }) =>
    request<{ user: User; csrfToken: string }>('/auth/login', json('POST', input)),
  accountLinks: (signal?: AbortSignal) =>
    request<AccountLinksOverview>('/auth/account-links', { signal }),
  accountLinkCapabilities: (signal?: AbortSignal) =>
    request<{ identity: string[]; connections: string[] }>('/integrations/capabilities', {
      signal,
    }),
  accountLinkChallenge: (
    provider: AccountLinkProvider,
    input: AccountLinkTarget,
    signal?: AbortSignal,
  ) =>
    request<AccountLinkChallenge>(`/auth/account-links/${provider}/challenge`, {
      ...json('POST', input),
      signal,
    }),
  verifyAccountLink: (
    provider: AccountLinkProvider,
    input: AccountLinkProof,
    signal?: AbortSignal,
  ) =>
    request<AccountLinkVerification>(`/auth/account-links/${provider}/verify`, {
      ...json('POST', input),
      signal,
    }),
  startAccountLink: (provider: AccountLinkProvider, stepUpToken: string, signal?: AbortSignal) =>
    request<{ authorizationUrl: string }>(`/auth/account-links/${provider}/start`, {
      ...json('POST', { stepUpToken }),
      signal,
    }),
  unlinkAccount: (
    provider: AccountLinkProvider,
    input: { identityId: string; stepUpToken: string },
    signal?: AbortSignal,
  ) =>
    request<AccountLinksOverview>(`/auth/account-links/${provider}`, {
      ...json('DELETE', input),
      signal,
    }),
  register: (input: { email: string; name: string; password: string; turnstileToken?: string }) =>
    request<{ user: User; csrfToken: string }>('/auth/register', json('POST', input)),
  logout: async () => {
    const bridge = desktopBridge();
    if (bridge) {
      if (!(await bridge.signOut())) throw new Error('退出已取消，草稿仍然保留。');
      return;
    }
    return request<void>('/auth/logout', json('POST'));
  },
  items: (params: URLSearchParams, signal?: AbortSignal) =>
    request<Page<LibraryItem>>(`/items?${params}`, { signal }),
  search: (input: LibrarySearchInput, signal?: AbortSignal) =>
    request<Page<LibraryItem>>('/search', { ...json('POST', input), signal }),
  item: (id: string, snapshotId?: string) =>
    request<ItemDetail>(
      `/items/${encodeURIComponent(id)}${snapshotId ? `?snapshotId=${encodeURIComponent(snapshotId)}` : ''}`,
    ),
  snapshots: (id: string) =>
    request<{ items: { id: string; createdAt: string }[] }>(
      `/items/${encodeURIComponent(id)}/snapshots`,
    ),
  capture: (input: CaptureInput, key: string) =>
    request<CaptureReceipt>('/captures', {
      ...json('POST', input),
      headers: { 'Idempotency-Key': key },
    }),
  patch: (id: string, patch: ItemPatch) =>
    request<LibraryItem>(`/items/${encodeURIComponent(id)}`, json('PATCH', patch)),
  batch: (
    ids: string[],
    patch: ItemPatch,
    options: Pick<BatchPatchInput, 'tagOperation' | 'mutationId'> = {},
  ) => request<{ items: LibraryItem[] }>('/items/batch', json('POST', { ids, patch, ...options })),
  remove: (id: string) => request<void>(`/items/${encodeURIComponent(id)}`, json('DELETE')),
  refresh: (id: string) =>
    isDesktop()
      ? startDesktopCapture({ intent: 'refresh', itemId: id, key: crypto.randomUUID() })
      : request<unknown>(`/items/${encodeURIComponent(id)}/refresh`, json('POST')),
  folders: () => request<{ items: Folder[] }>('/folders'),
  tags: () => request<{ items: Tag[] }>('/tags'),
  createFolder: (name: string, parentId: string | null = null) =>
    request<Folder>('/folders', json('POST', { name, parentId })),
  createTag: (name: string) => request<Tag>('/tags', json('POST', { name })),
  rename: (
    kind: 'folders' | 'tags',
    id: string,
    name: string,
    retained: { parentId?: string | null; color?: string | null } = {},
  ) => request<unknown>(`/${kind}/${encodeURIComponent(id)}`, json('PATCH', { name, ...retained })),
  removeGroup: (kind: 'folders' | 'tags', id: string) =>
    request<void>(`/${kind}/${encodeURIComponent(id)}`, json('DELETE')),
  settings: () => request<Settings>('/settings'),
  updateSettings: (value: Partial<Settings>) =>
    request<Settings>('/settings', json('PATCH', value)),
  usage: () => request<Usage>('/usage'),
  sessions: () => request<{ items: DeviceSession[] }>('/sessions'),
  revoke: (id: string) => request<void>(`/sessions/${encodeURIComponent(id)}`, json('DELETE')),
  import: (html: string) =>
    request<{ imported: number; duplicates: number; skipped: number }>(
      '/import',
      json('POST', { html }),
    ),
  export: () => request<{ jobId: string }>('/export', json('POST')),
  exportStatus: (id: string) =>
    request<{ status: string; url?: string; errorMessage?: string }>(
      `/exports/${encodeURIComponent(id)}`,
    ),
  approve: (flowId: string) =>
    request<{ redirectUrl: string }>('/auth/clients/approve', json('POST', { flowId })),
};

export function safeHttpUrl(value: string | null | undefined) {
  if (!value) return undefined;
  try {
    const url = new URL(value, location.origin);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}
export function platformName(value: string) {
  return (
    (
      {
        web: '网页',
        wechat: '公众号',
        zhihu: '知乎',
        xiaohongshu: '小红书',
        weibo: '微博',
        x: 'X',
        bilibili: '哔哩哔哩',
        youtube: 'YouTube',
      } as Record<string, string>
    )[value] || value
  );
}
export function statusLabel(item: LibraryItem) {
  if (item.status === 'queued') return '等待获取';
  if (item.status === 'running') return '正在获取';
  if (item.status === 'failed') return '获取未完成';
  if (item.quality === 'link_only') return '仅保存链接';
  if (item.quality === 'partial') return '部分保存';
  return '已保存';
}
export function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? ''
    : new Intl.DateTimeFormat('zh-CN', {
        month: 'short',
        day: 'numeric',
      }).format(date);
}
export function formatBytes(value: number) {
  return value >= 1024 ** 3
    ? `${(value / 1024 ** 3).toFixed(1)} GB`
    : value >= 1024 ** 2
      ? `${(value / 1024 ** 2).toFixed(1)} MB`
      : `${Math.ceil(value / 1024)} KB`;
}
